import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'crossroad';

export const IMAGE_REVEAL_DURATION = 400;
export const ZOOM_OUT_DURATION = 1500;
export const MARKER_LOOKUP_DELAY = 100;

export type TransitionStage = 'idle' | 'image-reveal' | 'zoom-out' | 'complete';

interface GeographicalTransitionState {
    isAnimating: boolean;
    stage: TransitionStage;
    sourceRect: DOMRect | null;
    mapCenter: { lat: number; lng: number } | null;
    markerPosition: { x: number; y: number } | null;
}

interface UseGeographicalTransitionOptions {
    passageId: string;
    reconstructionImageUrl?: string;
    placeName?: string | null;
    placeCoordinates?: { latitude: number; longitude: number } | null;
}

export function useGeographicalTransition({
    passageId,
    reconstructionImageUrl,
    placeName,
    placeCoordinates
}: UseGeographicalTransitionOptions) {
    const params = useParams('/app/passage/:id/context/:contextCategory');
    const contextCategory = params?.contextCategory;

    const [state, setState] = useState<GeographicalTransitionState>({
        isAnimating: false,
        stage: 'idle',
        sourceRect: null,
        mapCenter: null,
        markerPosition: null,
    });

    const hasTriggeredRef = useRef(false);
    const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
    // Track if the user was already in 'places' when passage changed
    const wasInPlacesRef = useRef(false);

    const clearAllTimeouts = useCallback(() => {
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];
    }, []);

    // Reset state when leaving the places context
    useEffect(() => {
        if (contextCategory !== 'places') {
            setState({
                isAnimating: false,
                stage: 'idle',
                sourceRect: null,
                mapCenter: null,
                markerPosition: null,
            });
            hasTriggeredRef.current = false;
            wasInPlacesRef.current = false;
            clearAllTimeouts();
        } else {
            // Mark that we're now in places context
            wasInPlacesRef.current = true;
        }
    }, [contextCategory, clearAllTimeouts]);

    // When passageId changes while in places context, reset the trigger
    // but mark that we should skip the reveal (just fly to new location)
    useEffect(() => {
        if (contextCategory === 'places') {
            // If we were already in places, this is an intra-map navigation
            // Reset trigger but the map will handle the fly animation itself
            hasTriggeredRef.current = false;
        }
    }, [passageId, contextCategory]);

    useEffect(() => {
        return () => clearAllTimeouts();
    }, [clearAllTimeouts]);

    // Auto-trigger transition when entering places context with a reconstruction image
    useEffect(() => {
        if (contextCategory === 'places' && reconstructionImageUrl && !hasTriggeredRef.current) {
            hasTriggeredRef.current = true;

            // 1. Prepare map center
            const mapCenter = placeCoordinates
                ? { lat: placeCoordinates.latitude, lng: placeCoordinates.longitude }
                : null;

            // 2. Capture source rect from the passage container
            const passageContainer = document.querySelector('[data-passage-text-container]');
            const sourceRect = passageContainer?.getBoundingClientRect() || null;

            // console.log('Auto-triggering "Image-to-Map" Transition');

            // 3. Start Stage 1: Image Reveal
            setState({
                isAnimating: true,
                stage: 'image-reveal',
                sourceRect,
                mapCenter,
                markerPosition: null,
            });

            // 4. Progress to Stage 2: Zoom Out
            const zoomOutTimeout = setTimeout(() => {
                // console.log('Transition: Advancing to zoom-out stage');
                setState(prev => ({ ...prev, stage: 'zoom-out' }));
            }, IMAGE_REVEAL_DURATION);
            timeoutsRef.current.push(zoomOutTimeout);

            // 5. Complete
            const completeTimeout = setTimeout(() => {
                // console.log('Transition: Advancing to complete stage');
                setState(prev => ({ ...prev, stage: 'complete' }));
            }, IMAGE_REVEAL_DURATION + ZOOM_OUT_DURATION);
            timeoutsRef.current.push(completeTimeout);
        }
    }, [contextCategory, reconstructionImageUrl, placeCoordinates, clearAllTimeouts]);

    const endAnimation = useCallback(() => {
        setState(prev => ({ ...prev, stage: 'complete', isAnimating: false }));
        clearAllTimeouts();
    }, [clearAllTimeouts]);

    const setMarkerPosition = useCallback((pos: { x: number; y: number }) => {
        setState(prev => ({ ...prev, markerPosition: pos }));
    }, []);

    return {
        ...state,
        setMarkerPosition,
        endAnimation,
        reconstructionImageUrl,
        placeName
    };
}
